import { useMemo } from 'react'
import Box from '@mui/material/Box'
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

interface ChatChartProps {
	content: string
}

interface ChartData {
	name: string
	fullName: string
	value: number
	label: string
	sortKey?: number
}

interface ParsedChart {
	data: ChartData[]
	variant: 'bar' | 'line'
}

const AXIS_LABEL_MAX = 18
const BAR_ROW_HEIGHT = 30
const MIN_BAR_CHART_HEIGHT = 160

function truncateLabel(text: string, max = AXIS_LABEL_MAX): string {
	if (text.length <= max) return text
	return `${text.slice(0, max - 1)}…`
}

function verticalBarChartHeight(barCount: number): number {
	return Math.max(MIN_BAR_CHART_HEIGHT, barCount * BAR_ROW_HEIGHT + 20)
}

function categoryAxisWidth(labels: string[]): number {
	const longest = Math.max(...labels.map((l) => l.length), AXIS_LABEL_MAX)
	return Math.min(152, Math.max(96, longest * 6.5 + 16))
}

function CategoryYAxisTick({ x = 0, y = 0, payload }: { x?: number; y?: number; payload?: { value?: string } }) {
	return (
		<text dy={4} fill="var(--mui-palette-text-secondary)" fontSize={11} textAnchor="end" x={x} y={y}>
			{payload?.value ?? ''}
		</text>
	)
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/
const PARTIAL_DAY = /\(so far\)$/i
const WEEKDAYS = new Set(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'])

function parseNumericCell(cell: string): number | null {
	const numericVal = cell.replace(/[$,€£¥%]/g, '').trim()
	if (numericVal === '' || Number.isNaN(Number(numericVal))) return null
	return parseFloat(numericVal)
}

function isTimeSeriesLabel(label: string): boolean {
	const trimmed = label.replace(PARTIAL_DAY, '').trim()
	if (ISO_DATE.test(trimmed)) return true
	if (WEEKDAYS.has(trimmed)) return true
	if (/^\d{1,2}(:\d{2})?$/.test(trimmed)) return true
	return false
}

function timeSortKey(label: string): number {
	const trimmed = label.replace(PARTIAL_DAY, '').trim()
	if (ISO_DATE.test(trimmed)) return new Date(trimmed).getTime()
	const weekdayIdx = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(trimmed)
	if (weekdayIdx >= 0) return weekdayIdx
	const hourMatch = trimmed.match(/^(\d{1,2})(?::(\d{2}))?$/)
	if (hourMatch) return Number(hourMatch[1]) * 60 + Number(hourMatch[2] ?? 0)
	return 0
}

function parseMarkdownTable(content: string): ParsedChart | null {
	const lines = content.split('\n').filter((l) => l.trim().startsWith('|'))
	if (lines.length < 3) return null

	const dataRows = lines.slice(2).filter((l) => !l.match(/^\|[\s-:|]+\|$/))

	if (dataRows.length < 2) return null

	const firstRow = dataRows[0]
		.split('|')
		.slice(1, -1)
		.map((c) => c.trim())
	let nameIdx = -1
	let valueIdx = -1

	for (let i = 0; i < firstRow.length; i++) {
		const cell = firstRow[i]
		if (nameIdx === -1 && parseNumericCell(cell) == null) {
			nameIdx = i
		} else if (valueIdx === -1 && parseNumericCell(cell) != null) {
			valueIdx = i
		}
	}

	if (nameIdx === -1 || valueIdx === -1) return null

	const data: ChartData[] = dataRows.slice(0, 10).map((row) => {
		const cells = row
			.split('|')
			.slice(1, -1)
			.map((c) => c.trim())
		const rawName = cells[nameIdx] ?? ''
		const rawVal = cells[valueIdx] ?? ''
		const value = parseNumericCell(rawVal) ?? 0
		return {
			name: truncateLabel(rawName),
			fullName: rawName,
			value,
			label: rawVal,
			sortKey: timeSortKey(rawName),
		}
	})

	if (data.length < 2) return null

	const rawNames = dataRows.slice(0, 10).map((row) => {
		const cells = row
			.split('|')
			.slice(1, -1)
			.map((c) => c.trim())
		return cells[nameIdx] ?? ''
	})
	const variant = rawNames.every((name) => isTimeSeriesLabel(name)) ? 'line' : 'bar'

	const sorted = variant === 'line' ? [...data].sort((a, b) => (a.sortKey ?? 0) - (b.sortKey ?? 0)) : data

	return { data: sorted, variant }
}

export function ChatChart({ content }: ChatChartProps) {
	const parsed = useMemo(() => parseMarkdownTable(content), [content])

	if (!parsed) return null

	const { data: chartData, variant } = parsed

	if (variant === 'line') {
		return (
			<Box sx={{ width: '100%', mt: 1.5, mb: 0.5 }}>
				<ResponsiveContainer height={180} width="100%">
					<LineChart data={chartData} margin={{ left: 0, right: 12, top: 4, bottom: 4 }}>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis
							dataKey="name"
							interval={0}
							tick={{ fontSize: 11 }}
							tickFormatter={(value: string) => truncateLabel(String(value), 10)}
						/>
						<YAxis tick={{ fontSize: 11 }} />
						<Tooltip
							contentStyle={{ fontSize: 12, borderRadius: 6 }}
							labelFormatter={(_label, payload) => String(payload?.[0]?.payload?.fullName ?? _label)}
							formatter={(value: number, _name: string, props: { payload?: ChartData }) => [
								props.payload?.label ?? value,
								'',
							]}
						/>
						<Line
							dataKey="value"
							dot={false}
							isAnimationActive={false}
							stroke="var(--mui-palette-primary-main)"
							strokeWidth={2}
							type="monotone"
						/>
					</LineChart>
				</ResponsiveContainer>
			</Box>
		)
	}

	return (
		<Box sx={{ width: '100%', mt: 1.5, mb: 0.5 }}>
			<ResponsiveContainer height={verticalBarChartHeight(chartData.length)} width="100%">
				<BarChart
					barCategoryGap="20%"
					data={chartData}
					layout="vertical"
					margin={{ left: 4, right: 12, top: 4, bottom: 4 }}
				>
					<CartesianGrid horizontal={false} strokeDasharray="3 3" />
					<XAxis type="number" tick={{ fontSize: 11 }} />
					<YAxis
						dataKey="name"
						interval={0}
						tick={<CategoryYAxisTick />}
						type="category"
						width={categoryAxisWidth(chartData.map((row) => row.fullName))}
					/>
					<Tooltip
						contentStyle={{ fontSize: 12, borderRadius: 6 }}
						labelFormatter={(_label, payload) => String(payload?.[0]?.payload?.fullName ?? _label)}
						formatter={(value: number, _name: string, props: { payload?: ChartData }) => [
							props.payload?.label ?? value,
							'',
						]}
					/>
					<Bar
						dataKey="value"
						fill="var(--mui-palette-primary-main)"
						isAnimationActive={false}
						maxBarSize={22}
						radius={[0, 4, 4, 0]}
					/>
				</BarChart>
			</ResponsiveContainer>
		</Box>
	)
}
